import { create } from 'zustand'
import { persist } from 'zustand/middleware'
import { projectAssetsApi, ProjectAsset, CreateProjectAssetRequest, UpdateProjectAssetRequest, GetProjectAssetsParams } from '../services/projectAssetsApi'

interface ProjectAssetsState {
  assets: ProjectAsset[]
  pagination: {
    total: number
    page: number
    limit: number
  }
  loading: boolean
  error: string | null
  currentProjectId: string | null
}

interface ProjectAssetsActions {
  fetchProjectAssets: (projectId: string, params?: GetProjectAssetsParams) => Promise<void>
  createProjectAsset: (assetData: CreateProjectAssetRequest) => Promise<void>
  updateProjectAsset: (assetId: string, assetData: UpdateProjectAssetRequest) => Promise<void>
  deleteProjectAsset: (assetId: string) => Promise<void>
  clearError: () => void
  setCurrentProject: (projectId: string | null) => void
}

type ProjectAssetsStore = ProjectAssetsState & ProjectAssetsActions

export const useProjectAssetsStore = create<ProjectAssetsStore>()(
  persist(
    (set) => ({
      assets: [],
      pagination: {
        total: 0,
        page: 1,
        limit: 10
      },
      loading: false,
      error: null,
      currentProjectId: null,

      fetchProjectAssets: async (projectId: string, params?: GetProjectAssetsParams) => {
        set({ loading: true, error: null, currentProjectId: projectId })
        try {
          const response = await projectAssetsApi.getProjectAssets(projectId, params)
          if (response.success && response.data) {
            // The API now returns paginated data, so we use it directly
            const assets = response.data || []
            
            set({
              assets: assets,
              pagination: {
                total: assets.length, // This will be updated when we get server-side pagination info
                page: params?.page || 1,
                limit: params?.limit || 10
              },
              loading: false,
              error: null,
            })
          } else {
            set({
              assets: [],
              pagination: { total: 0, page: 1, limit: 10 },
              loading: false,
              error: 'Failed to fetch project assets',
            })
          }
        } catch (error: any) {
          set({
            assets: [],
            pagination: { total: 0, page: 1, limit: 10 },
            loading: false,
            error: error.message || 'Failed to fetch project assets',
          })
        }
      },

      createProjectAsset: async (assetData: CreateProjectAssetRequest) => {
        set({ loading: true, error: null })
        try {
          const response = await projectAssetsApi.createProjectAsset(assetData)
          if (response.success && response.data) {
            const newAsset = response.data
            set(state => ({
              assets: [newAsset, ...state.assets],
              pagination: {
                ...state.pagination,
                total: state.pagination.total + 1
              },
              loading: false,
              error: null,
            }))
          } else {
            set({
              loading: false,
              error: 'Failed to create project asset',
            })
          }
        } catch (error: any) {
          set({
            loading: false,
            error: error.message || 'Failed to create project asset',
          })
        }
      },

      updateProjectAsset: async (assetId: string, assetData: UpdateProjectAssetRequest) => {
        set({ loading: true, error: null })
        try {
          const response = await projectAssetsApi.updateProjectAsset(assetId, assetData)
          if (response.success && response.data) {
            const updatedAsset = response.data
            set(state => ({
              assets: state.assets.map(asset => 
                asset._id === assetId ? updatedAsset : asset
              ),
              loading: false,
              error: null,
            }))
          } else {
            set({
              loading: false,
              error: 'Failed to update project asset',
            })
          }
        } catch (error: any) {
          set({
            loading: false,
            error: error.message || 'Failed to update project asset',
          })
        }
      },

      deleteProjectAsset: async (assetId: string) => {
        set({ loading: true, error: null })
        try {
          const response = await projectAssetsApi.deleteProjectAsset(assetId)
          if (response.success) {
            set(state => ({
              assets: state.assets.filter(asset => asset._id !== assetId),
              pagination: {
                ...state.pagination,
                total: Math.max(0, state.pagination.total - 1)
              },
              loading: false,
              error: null,
            }))
          } else {
            set({
              loading: false,
              error: 'Failed to delete project asset',
            })
          }
        } catch (error: any) {
          set({
            loading: false,
            error: error.message || 'Failed to delete project asset',
          })
        }
      },

      clearError: () => {
        set({ error: null })
      },

      setCurrentProject: (projectId: string | null) => {
        set({ currentProjectId: projectId })
      },
    }),
    {
      name: 'project-assets-storage',
      partialize: (state) => ({ 
        assets: state.assets,
        pagination: state.pagination,
        currentProjectId: state.currentProjectId
      }),
    }
  )
)
